import { HStack } from "@chakra-ui/react"
import { MouseEvent, ReactNode } from "react"
import Link, { LinkTo } from "../link/Link"
import Pathicon from "../pathicon/Pathicon"

type DotNavArrowProps = {
  to?: LinkTo
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

type DotNavProps<T> = {
  items: T[]
  activeIndex: number
  renderDot: (args: { item: T; index: number; isActive: boolean }) => ReactNode
  getItemKey?: (item: T, index: number) => string | number
  prev?: DotNavArrowProps
  next?: DotNavArrowProps
  className?: string
  dotsClassName?: string
  dotsGap?: string | number
}

type DotNavArrowDirection = "prev" | "next"
type DotNavArrowButtonProps = DotNavArrowProps & {
  direction: DotNavArrowDirection
}

const DotNavArrowButton = ({
  to,
  onClick,
  direction
}: DotNavArrowButtonProps) => {
  const ariaLabel = direction === "prev" ? "Previous" : "Next"
  const spatialDirection = direction === "prev" ? "left" : "right"
  const disabled = !to && !onClick

  return (
    <Link
      to={to}
      aria-label={ariaLabel}
      className={`Link DotNavArrow DotNavArrow--${spatialDirection}`}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      minW="20px"
      w="20px"
      h="20px"
      p={0}
      borderRadius="full"
      bgColor="transparent"
      color="white"
      textDecoration="none"
      _hover={{ bgColor: "transparent", color: "white" }}
      _active={{ bgColor: "transparent", color: "white" }}
      pointerEvents={disabled ? "none" : undefined}
      tabIndex={disabled ? -1 : undefined}
      aria-disabled={disabled || undefined}
      onClick={onClick}
    >
      <Pathicon
        icon={direction === "prev" ? "chevron-left" : "chevron-right"}
        size="0.85rem"
      />
    </Link>
  )
}

const DotNav = <T,>({
  items,
  activeIndex,
  renderDot,
  getItemKey,
  prev,
  next,
  className,
  dotsClassName = "DotNavBullets",
  dotsGap = 0
}: DotNavProps<T>) => {
  const rootClassName = ["DotNav", "DotNav__wrapper", className]
    .filter(Boolean)
    .join(" ")

  const prevNode = prev ? (
    <DotNavArrowButton {...prev} direction="prev" />
  ) : null
  const nextNode = next ? (
    <DotNavArrowButton {...next} direction="next" />
  ) : null

  return (
    <HStack
      className={rootClassName}
      justify="center"
      px="6px"
      py="3px"
      position="relative"
      gap={"1px"}
      css={{
        "&.DotNav__wrapper": {
          position: "relative"
        },
        "& .DotNavArrow": {
          position: "absolute",
          transform: "translateY(-50%)",
          top: "50%"
        },
        "& .DotNavArrow.DotNavArrow--left": {
          left: "-20px"
        },
        "& .DotNavArrow.DotNavArrow--right": {
          right: "-20px"
        },
        "& .DotNavBullets": {
          position: "relative",
          height: "20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        },
        "& .DotNavBullets .DotNavBullet": {
          margin: "0 2px",
          background: "transparent",
          color: "rgba(255, 255, 255, 0.35)",
          position: "relative",
          opacity: 1,
          border: "none",
          padding: 0,
          cursor: "pointer",
          lineHeight: 1,
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center"
        },
        "& .DotNavBullets .DotNavBullet .DotNavBullet__dot": {
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          border: "1px solid currentColor",
          background: "transparent",
          display: "block"
        },
        "& .DotNavBullets .DotNavBullet.DotNavBullet--complete .DotNavBullet__dot":
          {
            background: "currentColor"
          },
        "& .DotNavBullets .DotNavBullet.DotNavBullet--active": {
          color: "#fff"
        }
      }}
    >
      {prevNode}
      <HStack className={dotsClassName} gap={dotsGap}>
        {items.map((item, index) => (
          <span key={getItemKey ? getItemKey(item, index) : index}>
            {renderDot({
              item,
              index,
              isActive: index === activeIndex
            })}
          </span>
        ))}
      </HStack>
      {nextNode}
    </HStack>
  )
}

export default DotNav
